import { useState, useEffect } from "react";
import { Instagram, Twitter, Facebook, Send, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface SocialPrompt {
  headline: string;
  subtext: string;
  hashtag: string;
  category: 'purchase' | 'gifting';
}

const SOCIAL_PROMPTS: SocialPrompt[] = [
  // Gifting prompts
  {
    headline: "Made someone's day with a group gift! 🎁",
    subtext: "When friends come together, gifts become magical",
    hashtag: "#GroupGiftingWithShakatah #byShakatah",
    category: 'gifting'
  },
  {
    headline: "Plot twist: We all pitched in! 🎉",
    subtext: "The best gifts come from the best friends",
    hashtag: "#SplitTheGiftNotTheLove #byShakatah",
    category: 'gifting'
  },
  {
    headline: "Surprise! It was from ALL of us 💝",
    subtext: "One gift, many hearts, zero stress",
    hashtag: "#GroupGiftsAreBetterGifts #byShakatah",
    category: 'gifting'
  },
  {
    headline: "Chipped in, showed up, made memories! ✨",
    subtext: "Affordable for everyone, priceless for one",
    hashtag: "#ShakatahSquad #byShakatah",
    category: 'gifting'
  },
  // Purchase prompts
  {
    headline: "Why pay full price alone? 💸",
    subtext: "Split the cost, share the joy with friends",
    hashtag: "#SmartGroupBuying #byShakatah",
    category: 'purchase'
  },
  {
    headline: "Teamwork makes the dream work! 🛍️",
    subtext: "Group purchases = bigger savings",
    hashtag: "#BetterTogether #byShakatah",
    category: 'purchase'
  },
  {
    headline: "Splitting bills, not friendships 🤝",
    subtext: "Easy payments, happy everyone",
    hashtag: "#SplitWithShakatah #byShakatah",
    category: 'purchase'
  },
  {
    headline: "Big purchases, tiny payments! 💳",
    subtext: "Everyone pays their share, stress-free",
    hashtag: "#GroupBuyingMadeEasy #byShakatah",
    category: 'purchase'
  }
];

interface SocialShareSectionProps {
  shareUrl?: string;
  category?: 'purchase' | 'gifting' | 'all';
}

export function SocialShareSection({ 
  shareUrl = "https://www.shakatah.com/my-purchases",
  category = 'all'
}: SocialShareSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const filteredPrompts = category === 'all' 
    ? SOCIAL_PROMPTS 
    : SOCIAL_PROMPTS.filter(p => p.category === category);

  const currentPrompt = filteredPrompts[currentIndex];

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % filteredPrompts.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, filteredPrompts.length]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + filteredPrompts.length) % filteredPrompts.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % filteredPrompts.length);
  };

  const shareText = `${currentPrompt.headline} ${currentPrompt.subtext} ${currentPrompt.hashtag}`;

  const handleShare = (platform: 'whatsapp' | 'twitter' | 'facebook' | 'instagram') => {
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, '_blank');
        break;
      case 'instagram':
        window.open('https://www.instagram.com/explore/tags/groupgiftingwithshakatah/', '_blank');
        break;
    }
  };

  return (
    <Card className="overflow-hidden border border-border/50 shadow-sm bg-gradient-to-br from-[hsl(270,40%,94%)] to-[hsl(270,50%,88%)]">
      <CardContent className="p-0">
        <div className="relative overflow-hidden">
          <div className="relative p-5 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-xs font-medium mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              Share the Joy!
            </div>
            
            {/* Carousel Content */}
            <div className="relative min-h-[70px] flex items-center justify-center">
              <button 
                onClick={goToPrevious}
                className="absolute left-0 top-1/2 -translate-y-1/2 p-1 rounded-full bg-muted hover:bg-accent transition-colors text-muted-foreground"
                aria-label="Previous"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="px-8 transition-all duration-300">
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
                  {currentPrompt.headline}
                </h3>
                <p className="text-muted-foreground text-sm max-w-md mx-auto">
                  {currentPrompt.subtext}
                </p>
              </div>

              <button 
                onClick={goToNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-full bg-muted hover:bg-accent transition-colors text-muted-foreground"
                aria-label="Next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Carousel Dots */}
            <div className="flex justify-center gap-1.5 mt-2 mb-3">
              {filteredPrompts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentIndex(index);
                  }}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    index === currentIndex ? 'bg-foreground/60 w-3' : 'bg-foreground/20'
                  }`}
                  aria-label={`Go to prompt ${index + 1}`}
                />
              ))}
            </div>
            
            {/* Social Buttons - muted style */}
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button
                onClick={() => handleShare('whatsapp')}
                size="sm"
                className="text-xs gap-1.5 bg-[#25D366] hover:bg-[#1da851] text-white border-none"
              >
                <Send className="w-3.5 h-3.5" />
                WhatsApp
              </Button>
              
              <Button
                onClick={() => handleShare('instagram')}
                size="sm"
                className="text-xs gap-1.5 bg-gradient-to-r from-[#f09433] via-[#dc2743] to-[#bc1888] hover:opacity-90 text-white border-none"
              >
                <Instagram className="w-3.5 h-3.5" />
                Instagram
              </Button>
              
              <Button
                onClick={() => handleShare('twitter')}
                size="sm"
                className="text-xs gap-1.5 bg-[#000000] hover:bg-[#333333] text-white border-none"
              >
                <Twitter className="w-3.5 h-3.5" />
                X
              </Button>
              
              <Button
                onClick={() => handleShare('facebook')}
                size="sm"
                className="text-xs gap-1.5 bg-[#1877F2] hover:bg-[#0d65d9] text-white border-none"
              >
                <Facebook className="w-3.5 h-3.5" />
                Facebook
              </Button>
            </div>

            {/* Dynamic Hashtag */}
            <p className="mt-3 text-muted-foreground/60 text-xs">
              {currentPrompt.hashtag}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}